import { useLayoutEffect } from 'react';

import { type Bounds, getBounds, isNotDefined } from '@enterprise/core';

import type { ResizeCallback } from '#utilities/types/resize.callback.type';

import { useSyncedRef } from '#misc/synced-ref/synced-ref.hook';
import { getResizeObserver } from '#utilities/get-resize-observer';

import type { ResizeObserverOptions } from './types/resize-observer.options';

/**
 * Invokes a callback whenever ResizeObserver detects a change to target's size.
 *
 * @param {ResizeObserverOptions} options Options used in setting up the resize observer.
 * @param {boolean} options.enabled Whether resize observer is enabled or not.
 * @param {ResizeCallback} options.onResize BCallback that will be invoked on resize.lah
 * @param {RefObject<T> | T | null | undefined} options.target React reference or Element to track.
 */
export const useResizeObserver = <T extends Element>({
  enabled = true,
  onResize,
  round,
  target,
}: ResizeObserverOptions<T>): void => {
  const instance = enabled && getResizeObserver(round);
  const callbackRef = useSyncedRef(onResize);

  useLayoutEffect(() => {
    // this secondary target resolve required for case when we receive ref object, which, most
    // likely, contains null during render stage, but already populated with element during
    // effect stage.
    const targetElement = target?.current;

    if (!instance || isNotDefined(targetElement)) {
      return;
    }

    // as unsubscribe in internals of our ResizeObserver abstraction can
    // happen a bit later than effect cleanup invocation - we need a marker,
    // that this handler should not be invoked anymore
    let subscribed = true;

    const handler: ResizeCallback = (bounds: Bounds) => {
      // it is reinsurance for the highly asynchronous invocations, almost
      // impossible to achieve in tests, thus excluding from LOC
      if (subscribed) {
        callbackRef.current(bounds);
      }
    };

    const raf = requestAnimationFrame(() => {
      instance.add(targetElement, handler);
      callbackRef.current(getBounds(targetElement));
    });

    return () => {
      subscribed = false;
      cancelAnimationFrame(raf);
      instance.remove(targetElement, handler);
    };
  }, [callbackRef, instance, target?.current]);
};
